import { useFormik } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { updateUser } from "../../../utils/apis";
import formikTextFieldProps from "./formikTextFieldProps";

const validationSchema = Yup.object().shape({
  firstName: Yup.string().trim().required("First name is required").max(30),
  lastName: Yup.string().trim().max(30),
  email: Yup.string()
    .trim()
    .email("Enter a valid email")
    .required("Email is required")
    .max(100),
  companyName: Yup.string().trim().required("Company is required").max(30),
  appName: Yup.string().trim().max(30),
});

const EditUserDialog = ({ user, onClose, onUpdated }) => {
  const { enqueueSnackbar } = useSnackbar();

  const formik = useFormik({
    initialValues: {
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      companyName: user.companyName || "",
      appName: user.appName || "",
    },
    validationSchema,
    onSubmit: async (values) => {
      // the API rejects empty strings, so only changed, non-empty fields are sent
      const payload = {};
      Object.entries(values).forEach(([key, value]) => {
        const next = value.trim();
        if (next && next !== (user[key] || "")) payload[key] = next;
      });

      if (!Object.keys(payload).length) {
        onClose();
        return;
      }

      const res = await updateUser(user._id, payload, enqueueSnackbar);
      if (res) onUpdated();
    },
  });

  const field = (name, label, props = {}) => (
    <TextField
      id={`edit-user-${name}`}
      label={label}
      fullWidth
      {...formikTextFieldProps(formik, name)}
      {...props}
    />
  );

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit user</DialogTitle>
      <form onSubmit={formik.handleSubmit} noValidate>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5}>
              {field("firstName", "First name", { required: true, autoFocus: true })}
              {field("lastName", "Last name")}
            </Stack>
            {field("email", "Email", { type: "email", required: true })}
            {field("companyName", "Company", { required: true })}
            {field("appName", "App name")}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={formik.isSubmitting}>
            Save changes
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditUserDialog;
